import React, { useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Table,
  Space,
  PageHeader,
  Menu,
  Dropdown,
  Button,
  Tooltip,
} from "antd";
import {
  MoreOutlined,
  CloudDownloadOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { logisticListingSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import { reverse } from "named-urls";
import { useAuth } from "../../../../contexts/auth";

const { Column } = Table;

const MENU_ACTIONS = {
  viewLogisticPartnerDetails: "VIEW_LOGISTIC_PARTNER_DETAILS",
  editLogisticPartnerDetails: "EDIT_LOGISTIC_PARTNER_DETAILS",
  downloadContract: "DOWNLOAD_CONTRACT",
};

const LogisticListing = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: logisticListingSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;

  useEffect(() => {
    dispatch(actions.fetchLogistics());
  }, [dispatch]);

  const logistics = useSelector(selectors.selectlogistics) || [];

  const logisticData = logistics.map((logistic) => ({
    id: logistic._id,
    name: logistic.name,
    region:
      logistic?.operatingStates?.map((state) => state.name).join(", ") || "N/A",
    contactPhone: logistic.pocContact,
    contactName: logistic.pocName,
    contract: logistic.contract,
    remarks: logistic.remarks,
  }));

  const handleDownloadContract = (contractPath) => {
    if (!contractPath) {
      console.error("No contract path provided");
      return;
    }

    // Check if contract is a full URL (legacy data) or just a path
    if (contractPath.startsWith("http://") || contractPath.startsWith("https://")) {
      // Direct download for full URLs (legacy data)
      window.open(contractPath, "_blank");
    } else {
      // For blob paths like "labx-dev/CompanyName_1718103453000_a1b2c3.pdf"
      // Extract just the filename for the download saga
      const fileName = contractPath.includes("/") 
        ? contractPath.split("/").pop() 
        : contractPath;
      
      // Fetch fresh SAS token and download via saga
      dispatch(
        actions.downloadDocument({
          id: fileName,
          data: { fileName },
        })
      );
    }
  };

  const onClick = ({ key, id, data }) => {
    switch (key) {
      case MENU_ACTIONS.viewLogisticPartnerDetails:
        history.push(
          reverse(`${routes.backOffice.logisticPartners.details.view}`, {
            logisticId: id,
          })
        );
        break;
      case MENU_ACTIONS.editLogisticPartnerDetails:
        history.push(
          reverse(`${routes.backOffice.logisticPartners.details.edit}`, {
            logisticId: id,
          })
        );
        break;
      case MENU_ACTIONS.downloadContract:
        handleDownloadContract(data.contract);
        break;
      default:
        break;
    }
  };

  const getMenuItems = (logistic) => {
  return (
    <Menu
      onClick={({ key }) => onClick({ id: logistic.id, key, data: logistic })}
    >
      <Menu.Item
        key={MENU_ACTIONS.viewLogisticPartnerDetails}
        icon={<InfoCircleOutlined />}
      >
        View Logistic Partner Details
      </Menu.Item>

      {userRole !== "csc_admin" && (
        <>
          <Menu.Item
            key={MENU_ACTIONS.editLogisticPartnerDetails}
            icon={<EditOutlined />}
          >
            Edit Logistic Partner Details
          </Menu.Item>

          {/* Need to Work on Logistic Module */}
          {logistic.contract && logistic.contract.length > 0 && (
            <Menu.Item
              disabled={!(logistic?.contract !== "")}
              key={MENU_ACTIONS.downloadContract}
              icon={<CloudDownloadOutlined />}
            >
              {logistic?.contract !== ""
                ? "Download Contract"
                : "Upload Contract"}
            </Menu.Item>
          )}
        </>
      )}
    </Menu>
  );
};


  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Logistic Partners"
        extra={[
          userRole !== "support_team" && userRole !== "csc_admin" && (
          <Button key="1" type="primary">
            <Link to={routes.backOffice.logisticPartners.new}>
              Add New Logistic
            </Link>
          </Button>
          )
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table dataSource={logisticData}>
          <Column title="Logistic Partner" dataIndex="name" key="name" />
          <Column title="Region of Operation" dataIndex="region" key="region" />
          <Column
            title="Contact Number"
            dataIndex="contactPhone"
            key="contactPhone"
          />
          <Column
            title="Point of Contact"
            dataIndex="contactName"
            key="contactName"
          />
          {userRole !== "support_team" && (
            <Column
              title="Action"
              key="action"
              render={(text, record) => (
                <Space size="middle">
                  <Dropdown overlay={getMenuItems(record)}>
                    <Tooltip title="Actions">
                      <Button
                        type="text"
                        shape="circle"
                        icon={<MoreOutlined />}
                      />
                    </Tooltip>
                  </Dropdown>
                </Space>
              )}
            />
          )}
        </Table>
      </div>
    </div>
  );
};

export default LogisticListing;
